import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const INK = "#12100d";

/* 玄関背景C — 水鏡の月: ほのかな月暈と、夜の水面に揺れる月光の帯 */
export default function ApproachC() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, raf = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // 月光の帯（moonglade）: 水面に横縞のきらめきが並ぶ
    const MOON_X = 0.64; // 月の横位置（比率）
    const HORIZON = 0.55; // 水平線（比率）
    const N = 44;
    const streaks = Array.from({ length: N }, (_, i) => {
      const depth = i / (N - 1); // 0=水平線際, 1=手前
      return {
        depth,
        y: HORIZON + 0.02 + depth * 0.4,
        baseW: 14 + depth * 90, // 手前ほど帯が広がる
        f: 0.3 + Math.random() * 0.9,
        phase: Math.random() * Math.PI * 2,
        drift: (Math.random() - 0.5) * 14,
        a: (1 - depth * 0.55) * (0.05 + Math.random() * 0.1),
        th: 1 + depth * 2.2,
      };
    });

    const draw = (now) => {
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);
      for (const s of streaks) {
        const sway = reduced ? 0 : Math.sin(t * s.f + s.phase);
        const width = s.baseW * (0.65 + 0.35 * Math.abs(Math.sin(t * s.f * 0.7 + s.phase)));
        const x = MOON_X * w + (reduced ? 0 : sway * s.drift);
        const y = s.y * h;
        const alpha = s.a * (reduced ? 0.8 : 0.55 + 0.45 * Math.sin(t * s.f * 1.3 + s.phase * 2));
        if (alpha <= 0.004) continue;
        const g = ctx.createLinearGradient(x - width, y, x + width, y);
        g.addColorStop(0, "rgba(222,215,198,0)");
        g.addColorStop(0.5, `rgba(226,214,182,${alpha})`);
        g.addColorStop(1, "rgba(222,215,198,0)");
        ctx.fillStyle = g;
        ctx.fillRect(x - width, y - s.th / 2, width * 2, s.th);
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", bgcolor: INK }}>
      {/* 夜空の基調 */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, #1c1914 0%, #16130f 42%, #0e0c0a 56%, ${INK} 100%)`,
        }}
      />

      {/* 月暈（まぶしくない、にじむ光） */}
      <Box
        sx={{
          position: "absolute",
          left: "64%",
          top: "20%",
          width: "46vmin",
          height: "46vmin",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, rgba(236,229,216,0.16) 0%, rgba(226,210,170,0.07) 26%, rgba(212,180,110,0.03) 46%, rgba(212,180,110,0) 68%)",
          animation: "kodoMoonBreath 11s ease-in-out infinite alternate",
          "@keyframes kodoMoonBreath": {
            "0%": { opacity: 0.85 },
            "100%": { opacity: 1 },
          },
        }}
      />
      {/* 月の芯 */}
      <Box
        sx={{
          position: "absolute",
          left: "64%",
          top: "20%",
          width: "7vmin",
          height: "7vmin",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(240,232,214,0.5) 0%, rgba(236,229,216,0.18) 55%, rgba(236,229,216,0) 100%)",
          filter: "blur(2px)",
        }}
      />

      {/* 薄雲（月の前をゆっくり流れる） */}
      <Box
        sx={{
          position: "absolute",
          left: "-25%",
          top: "10%",
          width: "150%",
          height: "26%",
          background:
            "linear-gradient(100deg, rgba(27,24,21,0) 20%, rgba(27,24,21,0.5) 42%, rgba(27,24,21,0.15) 55%, rgba(27,24,21,0.55) 70%, rgba(27,24,21,0) 88%)",
          filter: "blur(10px)",
          animation: "kodoMoonCloud 70s linear infinite alternate",
          "@keyframes kodoMoonCloud": {
            "0%": { transform: "translateX(-4%)" },
            "100%": { transform: "translateX(7%)" },
          },
        }}
      />

      {/* 水面（水平線から下） */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "55%",
          bottom: 0,
          background: `linear-gradient(180deg, #0e0c0a 0%, #110f0c 30%, ${INK} 100%)`,
          borderTop: "1px solid rgba(222,215,198,0.035)",
        }}
      />

      {/* 月光の帯（canvas: 揺れるきらめき） */}
      <Box component="canvas" ref={canvasRef} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* 岸辺の霧 */}
      <Box
        sx={{
          position: "absolute",
          left: "-15%",
          top: "50%",
          width: "130%",
          height: "14%",
          background: "radial-gradient(ellipse 50% 55% at 50% 50%, rgba(222,215,198,0.04), rgba(222,215,198,0) 70%)",
          filter: "blur(8px)",
          animation: "kodoLakeMist 48s ease-in-out infinite alternate",
          "@keyframes kodoLakeMist": {
            "0%": { transform: "translateX(-5%)" },
            "100%": { transform: "translateX(8%)" },
          },
        }}
      />

      {/* 手前の暗がり（文字の可読性） */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 120% 90% at 50% 115%, rgba(0,0,0,0.5), rgba(0,0,0,0) 55%)",
        }}
      />
    </Box>
  );
}
